import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '../api-routes.js'
import type { ApiResponse, KnowledgeBase } from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface CreateKnowledgeBaseRequest {
  title: string
  body: string
}

export interface UpdateKnowledgeBaseRequest {
  title?: string
  body?: string
}

export class KnowledgeApiClient extends BaseApiClient {
  /**
   * Get all knowledge bases
   * @returns KnowledgeBase[]
   */
  async getKnowledgeBases(): Promise<KnowledgeBase[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase[]>>(
          API_ROUTES.KNOWLEDGE_BASES.GET_ALL,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching knowledge bases',
      },
    )
    return result || []
  }

  /**
   * Get a specific knowledge base by ID
   * @param id - The ID of the knowledge base to get
   * @returns KnowledgeBase | null
   */
  async getKnowledgeBase(id: string): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.GET, { id }),
        )
        return response.data || null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching knowledge base',
      },
    )
  }

  /**
   * Create a new knowledge base
   * @param data - The data for the new knowledge base
   * @returns KnowledgeBase | null
   */
  async createKnowledgeBase(
    data: CreateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          API_ROUTES.KNOWLEDGE_BASES.CREATE,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating knowledge base',
      },
    )
  }

  /**
   * Update an existing knowledge base
   * @param id - The ID of the knowledge base to update
   * @param data - The data to update the knowledge base with
   * @returns KnowledgeBase | null
   */
  async updateKnowledgeBase(
    id: string,
    data: UpdateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.UPDATE, { id }),
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating knowledge base',
      },
    )
  }

  /**
   * Delete a knowledge base
   * @param id - The ID of the knowledge base to delete
   * @returns { success: boolean; message: string } | null
   */
  async deleteKnowledgeBase(
    id: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.DELETE, { id }), {
          method: 'DELETE',
        })
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting knowledge base',
      },
    )
  }

  /**
   * Get knowledge bases for a specific company (Super Admin only)
   * @param companyId - The ID of the company to get knowledge bases for
   * @returns KnowledgeBase[]
   */
  async getKnowledgeBasesForCompany(
    companyId: string,
  ): Promise<KnowledgeBase[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase[]>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.GET_ALL}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching knowledge bases for company',
      },
    )
    return result || []
  }

  /**
   * Create knowledge base for a specific company (Super Admin only)
   * @param companyId - The ID of the company to create the knowledge base for
   * @param data - The data for the new knowledge base
   * @returns KnowledgeBase | null
   */
  async createKnowledgeBaseForCompany(
    companyId: string,
    data: CreateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.CREATE}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating knowledge base for company',
      },
    )
  }

  /**
   * Update knowledge base for a specific company (Super Admin only)
   * @param id - The ID of the knowledge base to update
   * @param companyId - The ID of the company to update the knowledge base for
   * @param data - The data to update the knowledge base with
   * @returns KnowledgeBase | null
   */
  async updateKnowledgeBaseForCompany(
    id: string,
    companyId: string,
    data: UpdateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.UPDATE, { id })}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating knowledge base for company',
      },
    )
  }

  /**
   * Delete knowledge base for a specific company (Super Admin only)
   * @param id - The ID of the knowledge base to delete
   * @param companyId - The ID of the company to delete the knowledge base for
   * @returns { success: boolean; message: string } | null
   */
  async deleteKnowledgeBaseForCompany(
    id: string,
    companyId: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.DELETE, { id })}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting knowledge base for company',
      },
    )
  }
}

// Export a singleton instance
export const knowledgeApiClient = new KnowledgeApiClient()
